import flatten from 'lodash/flatten';
import camelCase from 'lodash/camelCase';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { ModuleType } from '../../../types/modules';
import { withOffset } from '@wix/da-shared-react/pkg/Stream';
import { userSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/user';
import { groupSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/group';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import { actions as threadActions } from '@wix/da-shared-react/pkg/CommentThread';
import { getCommentableTypeForDeviationType } from '@wix/da-shared-react/pkg/DeviationPage/redux/helpers';
import { initializeThread } from '@wix/da-shared-react/pkg/CommentThread/actionCreators';
import { fetchModuleDataSuccess } from '../../actions/modules';
import {
  fetchProfileOwnerTaglineSuccess,
  fetchProfileOwnerStatsSuccess,
} from '../../actions/profileOwner';
import { GallectionType } from '../../../types/gallection';
import {
  MY_COMMENTS_STREAM,
  MY_COMMENTS_STREAM_ITEMS_PER_FETCH,
  BADGE_ACTIVITY_STREAM,
  BADGE_ACTIVITY_STREAM_ITEMS_PER_FETCH,
  GROUPS_ADMIN_IDS_STREAM,
  GROUPS_MEMBER_IDS_STREAM,
  GROUPS_PER_FETCH,
  USERS_PER_FETCH,
  WATCHER_IDS_STREAM,
  WATCHING_IDS_STREAM,
  VISITOR_IDS_STREAM,
  GALLECTION_FOLDERS_PER_FETCH,
} from '../../streams';

import { PostsType, JournalFilterType } from '../../../types/posts';
import { ShopItemType } from '../../../types/shop';

import {
  getPostsDeviationsStreamId,
  POSTS_DEVIATIONS_STREAM_GROUP_ID,
  POSTS_PER_FETCH,
  FULLTEXT_JOURNALS_PER_FETCH,
  FEED_COMMENTS_MAX_DEPTH,
  FEED_COMMENTS_ITEMS_PER_FETCH,
} from '../../streams/posts';

import {
  getShopItemsStreamId,
  SHOP_ITEMS_STREAM_GROUP_ID,
  SHOP_ITEMS_PER_FETCH,
} from '../../streams/shop';

import { getModuleByType, getModulesByType } from './common';
import {
  PapiModuleGallectionFolders,
  PapiModuleData,
  PapiDeviation,
} from '@wix/da-types-papi';
import {
  normalizeGallectionList,
  getStreamIdForGallectionType,
} from '../../utils/gallection';

function _getDefaultModuleInitAction(modules, moduleType) {
  const module = getModuleByType(modules, moduleType);
  if (!module) {
    return null;
  }
  const { id, type, version, privileges, moduleData } = module;
  return fetchModuleDataSuccess({
    id,
    type,
    version,
    privileges,
    moduleData,
  });
}

function _getDeviationsModuleInitAction(module, deviationsKey = 'deviations') {
  const { id, type, version, privileges, moduleData } = module;
  const normalized = normalize(moduleData[deviationsKey] || [], [
    deviationSchema,
  ]);
  return fetchModuleDataSuccess({
    id,
    type,
    version,
    privileges,
    moduleData: {
      ...moduleData,
      [deviationsKey]: normalized.result,
    },
    entities: normalized.entities,
  });
}

export function sanitizeModule(module) {
  if (module.type) {
    // already converted to internal format
    return module;
  }
  const sanitizedData = module.moduleData[camelCase(module.moduleData.dataKey)];
  if (
    sanitizedData.galleryId &&
    sanitizedData.galleryUrl &&
    sanitizedData.premiumFolderData
  ) {
    sanitizedData.premiumFolderData.galleryId = sanitizedData.galleryId;
    sanitizedData.premiumFolderData.galleryUrl = sanitizedData.galleryUrl;
  }
  return {
    id: module.id,
    type: module.name,
    version: module.version,
    privileges: module.privileges,
    moduleData: sanitizedData,
  };
}

export function getCoverDeviationModuleInitAction(modules) {
  const module = getModuleByType(modules, ModuleType.COVER_DEVIATION);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { coverDeviation, coverDeviationidOffsetY } = moduleData;

    return fetchModuleDataSuccess({
      id,
      type,
      version,
      privileges,
      moduleData: {
        coverDeviation,
        coverDeviationidOffsetY,
      },
    });
  }
}

export function getFeaturedDeviationModuleInitAction(modules) {
  const featuredDeviationModules = getModulesByType(
    modules,
    ModuleType.FEATURED_DEVIATION
  );
  if (featuredDeviationModules.length) {
    return featuredDeviationModules.map(module => {
      const { id, type, version, privileges, moduleData } = module;
      const { deviation, title } = moduleData;
      const normalized = deviation
        ? normalize(deviation, deviationSchema)
        : { result: null, entities: null };

      return fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
        moduleData: {
          title,
          deviation: normalized.result,
        },
        entities: normalized.entities,
      });
    });
  }
}

export function getExhibitionistModuleInitAction(modules) {
  const exhibitionistModules = getModulesByType(
    modules,
    ModuleType.EXHIBITIONIST
  );
  if (exhibitionistModules.length) {
    return exhibitionistModules.map(module =>
      _getDeviationsModuleInitAction(module, 'deviations')
    );
  }
}

export function getAboutMeInitAction(modules) {
  const module = getModuleByType(modules, ModuleType.ABOUT);
  if (!module) {
    return null;
  }

  const { id, type, version, privileges, moduleData = {} } = module;

  return [
    fetchModuleDataSuccess({
      id,
      type,
      version,
      privileges,
      moduleData,
    }),
    fetchProfileOwnerTaglineSuccess({ tagline: moduleData.tagline }),
  ];
}

export function getDonationsInitAction(modules) {
  return _getDefaultModuleInitAction(modules, ModuleType.DONATIONS);
}

export function getDeviousnessInitAction(modules) {
  return _getDefaultModuleInitAction(modules, ModuleType.DEVIOUSNESS);
}

export function getVisitorsInitAction(modules) {
  const module = getModuleByType(modules, ModuleType.VISITORS);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { result, entities } = normalize(moduleData.results, [userSchema]);
    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
      }),
      withOffset.actions.initialize({
        streamId: VISITOR_IDS_STREAM,
        streamParams: {
          moduleId: id,
        },
        itemsPerFetch: USERS_PER_FETCH,
        items: result,
        entities,
        hasMore: false,
      }),
    ];
  }
}

export function getCustomBoxInitAction(modules) {
  const customboxModules = getModulesByType(modules, ModuleType.CUSTOM);
  if (customboxModules.length) {
    return customboxModules.map(module => {
      const { id, type, version, privileges, moduleData } = module;
      const { image } = moduleData;
      const normalized = image
        ? normalize(image, deviationSchema)
        : { result: null, entities: null };

      return fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
        moduleData: {
          ...moduleData,
          image: normalized.result,
        },
        entities: normalized.entities,
      });
    });
  }
}

export function getUserstatsInitAction(modules) {
  return _getDefaultModuleInitAction(modules, ModuleType.STATS);
}

export function getFoldersModuleInitAction(modules, requestParams) {
  const module = getModuleByType(modules, ModuleType.FOLDERS) as
    | PapiModuleData
    | undefined;
  const moduleData = module?.moduleData as
    | PapiModuleGallectionFolders
    | undefined;
  // auto-installing doesn't send gallection type, so gotta infer
  const gallectionType =
    requestParams.gallectionType ??
    (moduleData?.results?.[0]?.type as GallectionType);

  if (!module || !gallectionType || !moduleData) {
    return [];
  }

  const { results: folders } = moduleData;

  const { result, entities } = normalizeGallectionList(folders, gallectionType);

  return withOffset.actions.initialize({
    streamId: getStreamIdForGallectionType(gallectionType),
    streamParams: {
      moduleId: module.id,
      gallectionType,
    },
    itemsPerFetch: GALLECTION_FOLDERS_PER_FETCH,
    items: result,
    entities,
    hasMore: moduleData.hasMore,
    nextOffset: moduleData.nextOffset ?? undefined,
    hasLess: moduleData.hasLess,
    prevOffset: moduleData.prevOffset,
  });
}

export function getWatchersModuleInitActions(modules) {
  const module = getModuleByType(modules, ModuleType.WATCHERS);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { result, entities } = normalize(moduleData.results, [userSchema]);
    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
      }),
      withOffset.actions.initialize({
        streamId: WATCHER_IDS_STREAM,
        streamParams: {
          moduleId: id,
          canSeeAll: moduleData.canSeeAll,
        },
        itemsPerFetch: USERS_PER_FETCH,
        items: result,
        entities,
        hasMore: moduleData.hasMore,
      }),
    ];
  }
}

export function getGroupsAdminModuleInitActions(modules) {
  const module = getModuleByType(modules, ModuleType.GROUPS_ADMIN);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { result, entities } = normalize(moduleData.results, [groupSchema]);
    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
        moduleData: {
          total: moduleData.total,
        },
      }),
      withOffset.actions.initialize({
        streamId: GROUPS_ADMIN_IDS_STREAM,
        streamParams: {
          moduleId: id,
        },
        itemsPerFetch: GROUPS_PER_FETCH,
        items: result,
        entities,
        hasMore: moduleData.hasMore,
        total: moduleData.total,
      }),
      fetchProfileOwnerStatsSuccess({
        groupsAdmin: moduleData.total,
      }),
    ];
  }
}

export function getActiveMemberModuleInitActions(modules) {
  return _getDefaultModuleInitAction(modules, ModuleType.ACTIVE_MEMBERS);
}

export function getGroupsMemberModuleInitActions(modules) {
  const module = getModuleByType(modules, ModuleType.GROUPS_MEMBER);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { result, entities } = normalize(moduleData.results, [groupSchema]);
    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
        moduleData: {
          total: moduleData.total,
        },
      }),
      withOffset.actions.initialize({
        streamId: GROUPS_MEMBER_IDS_STREAM,
        streamParams: {
          moduleId: id,
        },
        itemsPerFetch: GROUPS_PER_FETCH,
        items: result,
        entities,
        hasMore: moduleData.hasMore,
        total: moduleData.total,
      }),
      fetchProfileOwnerStatsSuccess({
        groupsMember: moduleData.total,
      }),
    ];
  }
}

export function getWatchingModuleInitActions(modules) {
  const module = getModuleByType(modules, ModuleType.WATCHING);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { result, entities } = normalize(moduleData.results, [userSchema]);
    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
      }),
      withOffset.actions.initialize({
        streamId: WATCHING_IDS_STREAM,
        streamParams: {
          moduleId: id,
          canSeeAll: moduleData.canSeeAll,
        },
        itemsPerFetch: USERS_PER_FETCH,
        items: result,
        entities,
        hasMore: moduleData.hasMore,
      }),
    ];
  }
}

export function getBadgeActivityModuleInitActions(modules) {
  const module = getModuleByType(modules, ModuleType.BADGES_ACTIVITY);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const {
      totalSent,
      totalReceived,
      results,
      hasMore,
      nextOffset,
    } = moduleData;
    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
        moduleData: {
          totalSent,
          totalReceived,
        },
      }),
      withOffset.actions.initialize({
        streamId: BADGE_ACTIVITY_STREAM,
        streamParams: {
          moduleId: id,
        },
        itemsPerFetch: BADGE_ACTIVITY_STREAM_ITEMS_PER_FETCH,
        items: results,
        nextOffset,
        hasMore,
      }),
      fetchProfileOwnerStatsSuccess({
        badgesSent: totalSent,
        badgesReceived: totalReceived,
      }),
    ];
  }
}

export function getMyCommentsModuleInitActions(modules) {
  const module = getModuleByType(modules, ModuleType.MY_COMMENTS);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { results, hasMore, nextOffset } = moduleData;
    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
        moduleData: {},
      }),
      withOffset.actions.initialize({
        streamId: MY_COMMENTS_STREAM,
        streamParams: {
          moduleId: id,
        },
        itemsPerFetch: MY_COMMENTS_STREAM_ITEMS_PER_FETCH,
        items: results,
        nextOffset,
        hasMore,
      }),
    ];
  }
}

export function getFeedInitAction(modules) {
  const module = getModuleByType(modules, ModuleType.FEED);
  if (module) {
    return _getDeviationsModuleInitAction(module, 'results');
  }
}

export function getFeedPinnedDeviationInitAction(modules) {
  return _getDefaultModuleInitAction(modules, ModuleType.FEED_PINNED_DEVIATION);
}

// TODO: consolidate deviation stream init actions
export function getJournalsInitAction(
  modules,
  requestParams,
  features: string[] = []
) {
  const module = getModuleByType(modules, ModuleType.JOURNALS);
  if (!module) {
    return null;
  }
  const { id, type, version, privileges, moduleData } = module;
  const deviations: PapiDeviation[] = moduleData.results ?? [];
  const normalized = normalize(deviations, [deviationSchema]);

  const streamId = getPostsDeviationsStreamId({
    type: PostsType.JOURNALS,
    sortOrder: requestParams.order,
  });

  return [
    fetchModuleDataSuccess({
      id,
      type,
      version,
      privileges,
    }),
    withOffset.actions.initialize({
      streamId,
      groupId: POSTS_DEVIATIONS_STREAM_GROUP_ID,
      itemsPerFetch: FULLTEXT_JOURNALS_PER_FETCH,
      streamParams: {
        moduleId: id,
        postsType: PostsType.JOURNALS,
        sortOrder: requestParams.order,
        filter: JournalFilterType.ALL,
      },
      nextOffset: moduleData.nextOffset,
      items: normalized.result,
      entities: normalized.entities,
      hasMore: moduleData.hasMore,
      total: moduleData.total,
    }),
    ...(deviations?.map(deviation =>
      initializeThread({
        thread: {
          commentableItemId: deviation.deviationId,
          commentableTypeId: getCommentableTypeForDeviationType(
            deviation.typeId
          ),
          items: [],
          hasMore: !!deviation.stats?.comments,
          total: deviation.stats?.comments ?? 0,
          canPostComment: !!deviation.isCommentable,
        },
        maxDepth: FEED_COMMENTS_MAX_DEPTH,
        ...(features.includes('postfeed_longer_comment_pages') && {
          itemsPerFetch: FEED_COMMENTS_ITEMS_PER_FETCH,
        }),
      })
    ) ?? []),
  ];
}

export function getFeaturedJournalsInitAction(modules) {
  const module = getModuleByType(modules, ModuleType.FEATURED_JOURNALS);
  if (!module) {
    return null;
  }
  const { id, type, version, privileges, moduleData } = module;
  const normalized = normalize(moduleData.results || [], [deviationSchema]);

  const streamId = getPostsDeviationsStreamId({
    type: PostsType.JOURNALS,
    filter: JournalFilterType.FEATURED,
  });

  return [
    fetchModuleDataSuccess({
      id,
      type,
      version,
      privileges,
    }),
    withOffset.actions.initialize({
      streamId,
      groupId: POSTS_DEVIATIONS_STREAM_GROUP_ID,
      itemsPerFetch: POSTS_PER_FETCH,
      streamParams: {
        moduleId: id,
        postsType: PostsType.JOURNALS,
        filter: JournalFilterType.FEATURED,
      },
      nextOffset: moduleData.nextOffset,
      items: normalized.result,
      entities: normalized.entities,
      hasMore: moduleData.hasMore,
      total: moduleData.total,
    }),
  ];
}

export function getPollsInitAction(modules, requestParams) {
  const module = getModuleByType(modules, ModuleType.POLLS);
  if (!module) {
    return null;
  }
  const { id, type, version, privileges, moduleData } = module;
  const normalized = normalize(moduleData.results || [], [deviationSchema]);

  const streamId = getPostsDeviationsStreamId({
    type: PostsType.POLLS,
    sortOrder: requestParams.order,
  });

  return [
    fetchModuleDataSuccess({
      id,
      type,
      version,
      privileges,
    }),
    withOffset.actions.initialize({
      streamId,
      groupId: POSTS_DEVIATIONS_STREAM_GROUP_ID,
      itemsPerFetch: POSTS_PER_FETCH,
      streamParams: {
        moduleId: id,
        postsType: PostsType.POLLS,
        sortOrder: requestParams.order,
      },
      nextOffset: moduleData.nextOffset,
      items: normalized.result,
      entities: normalized.entities,
      hasMore: moduleData.hasMore,
      total: moduleData.total,
    }),
  ];
}

export function getStatusesInitAction(modules, requestParams) {
  const module = getModuleByType(modules, ModuleType.STATUSES);
  if (!module) {
    return null;
  }
  const { id, type, version, privileges, moduleData } = module;
  const normalized = normalize(moduleData.results || [], [deviationSchema]);

  const streamId = getPostsDeviationsStreamId({
    type: PostsType.STATUSES,
    sortOrder: requestParams.order,
  });

  return [
    fetchModuleDataSuccess({
      id,
      type,
      version,
      privileges,
    }),
    withOffset.actions.initialize({
      streamId,
      groupId: POSTS_DEVIATIONS_STREAM_GROUP_ID,
      itemsPerFetch: POSTS_PER_FETCH,
      streamParams: {
        moduleId: id,
        postsType: PostsType.STATUSES,
        sortOrder: requestParams.order,
      },
      nextOffset: moduleData.nextOffset,
      items: normalized.result,
      entities: normalized.entities,
      hasMore: moduleData.hasMore,
      total: moduleData.total,
    }),
  ];
}

export function getDraftsInitAction(modules, requestParams) {
  const module = getModuleByType(modules, ModuleType.DRAFTS);
  if (!module) {
    return null;
  }
  const { id, type, version, privileges, moduleData } = module;
  const normalized = normalize(moduleData.results || [], [deviationSchema]);

  const streamId = getPostsDeviationsStreamId({
    type: PostsType.DRAFTS,
    sortOrder: requestParams.order,
  });

  return [
    fetchModuleDataSuccess({
      id,
      type,
      version,
      privileges,
    }),
    withOffset.actions.initialize({
      streamId,
      groupId: POSTS_DEVIATIONS_STREAM_GROUP_ID,
      itemsPerFetch: POSTS_PER_FETCH,
      streamParams: {
        moduleId: id,
        postsType: PostsType.DRAFTS,
        sortOrder: requestParams.order,
      },
      nextOffset: moduleData.nextOffset,
      items: normalized.result,
      entities: normalized.entities,
      hasMore: moduleData.hasMore,
      total: moduleData.total,
    }),
  ];
}

export const allAutoInstallableModulesInitActions = {
  getAboutMeInitAction,
  getBadgeActivityModuleInitActions,
  getCommissionsModuleInitAction,
  getCoverDeviationModuleInitAction,
  getCustomBoxInitAction,
  getDonationsInitAction,
  getDeviousnessInitAction,
  getDraftsInitAction,
  getExhibitionistModuleInitAction,
  getFeaturedDeviationModuleInitAction,
  getFeaturedJournalsInitAction,
  getFeedInitAction,
  getFeedPinnedDeviationInitAction,
  getFoldersModuleInitActions: getFoldersModuleInitAction,
  getActiveMemberModuleInitActions,
  getGroupsAdminModuleInitActions,
  getGroupsMemberModuleInitActions,
  getJournalsInitAction,
  getMyCommentsModuleInitActions,
  getCommentsInitActions,
  getPollsInitAction,
  getPremiumContentModuleInitAction,
  getPrintsModuleInitAction,
  getPremiumFoldersModuleInitAction,
  getStatusesInitAction,
  getUserstatsInitAction,
  getVisitorsInitAction,
  getWatchersModuleInitActions,
  getWatchingModuleInitActions,
};

export function getCommentsInitActions(modules, requestParams) {
  const module = getModuleByType(modules, ModuleType.COMMENTS);
  if (!module) {
    return [];
  }
  const { id, type, version, privileges, moduleData } = module;
  const {
    commentableTypeid: commentableTypeId,
    commentableItemid: commentableItemId,
    thread: items,
    total,
    hasMore,
    canPostComment,
    cursor,
  } = moduleData;
  return [
    fetchModuleDataSuccess({
      id,
      type,
      version,
      privileges,
    }),
    threadActions.initializeThread({
      thread: {
        commentableTypeId,
        commentableItemId,
        items,
        // force "newest" sort order for comments on user profile
        order: requestParams.commentOrder || 'newest',
        total,
        hasMore,
        canPostComment,
        cursor,
      },
      maxDepth: requestParams.commentMaxDepth,
    }),
  ];
}

export function getCommissionsModuleInitAction(modules) {
  const module = getModuleByType(modules, ModuleType.COMMISSIONS);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { result, entities } = normalize(moduleData.results || [], [
      deviationSchema,
    ]);

    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
        moduleData: {
          isOpenForCommissions: moduleData.isOpenForCommissions,
        },
      }),
      withOffset.actions.initialize({
        streamId: getShopItemsStreamId(ShopItemType.COMMISSIONS),
        groupId: SHOP_ITEMS_STREAM_GROUP_ID,
        streamParams: {
          moduleId: id,
          shopItemType: ShopItemType.COMMISSIONS,
        },
        itemsPerFetch: SHOP_ITEMS_PER_FETCH,
        items: result,
        entities,
        hasMore: moduleData.hasMore,
        total: moduleData.total,
      }),
    ];
  }
}

export function getPrintsModuleInitAction(modules) {
  const module = getModuleByType(modules, ModuleType.PRINTS);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { result, entities } = normalize(moduleData.results || [], [
      deviationSchema,
    ]);

    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
      }),
      withOffset.actions.initialize({
        streamId: getShopItemsStreamId(ShopItemType.PRINTS),
        groupId: SHOP_ITEMS_STREAM_GROUP_ID,
        streamParams: {
          moduleId: id,
          shopItemType: ShopItemType.PRINTS,
        },
        itemsPerFetch: SHOP_ITEMS_PER_FETCH,
        items: result,
        entities,
        hasMore: moduleData.hasMore,
        total: moduleData.total,
      }),
    ];
  }
}

export function getPremiumFoldersModuleInitAction(modules) {
  const module = getModuleByType(modules, ModuleType.PREMIUM_FOLDERS);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { result, entities } = normalizeGallectionList(
      moduleData.results || [],
      GallectionType.GALLERY
    );

    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
      }),
      withOffset.actions.initialize({
        streamId: getShopItemsStreamId(ShopItemType.PREMIUM_FOLDERS),
        groupId: SHOP_ITEMS_STREAM_GROUP_ID,
        streamParams: {
          moduleId: id,
          shopItemType: ShopItemType.PREMIUM_FOLDERS,
        },
        itemsPerFetch: SHOP_ITEMS_PER_FETCH,
        items: result,
        entities,
        hasMore: moduleData.hasMore,
        total: moduleData.total,
      }),
    ];
  }
}

export function getPremiumContentModuleInitAction(modules) {
  const module = getModuleByType(modules, ModuleType.PREMIUM_CONTENT);
  if (module) {
    const { id, type, version, privileges, moduleData } = module;
    const { result, entities } = normalize(moduleData.results || [], [
      deviationSchema,
    ]);
    return [
      fetchModuleDataSuccess({
        id,
        type,
        version,
        privileges,
      }),
      withOffset.actions.initialize({
        streamId: getShopItemsStreamId(ShopItemType.PREMIUM_CONTENT),
        groupId: SHOP_ITEMS_STREAM_GROUP_ID,
        streamParams: {
          moduleId: id,
          shopItemType: ShopItemType.PREMIUM_CONTENT,
        },
        itemsPerFetch: SHOP_ITEMS_PER_FETCH,
        items: result,
        entities,
        hasMore: moduleData.hasMore,
        total: moduleData.total,
      }),
    ];
  }
}

export function getModuleInitActions(
  singleModule,
  requestParams = {},
  features = []
) {
  return getModulesInitActions([singleModule], requestParams, features);
}

export function getModulesInitActions(
  modules,
  requestParams = {},
  features = []
) {
  const standardizedModules = modules.map(sanitizeModule);
  const actions = Object.keys(allAutoInstallableModulesInitActions).map(key =>
    allAutoInstallableModulesInitActions[key](
      standardizedModules,
      requestParams,
      features
    )
  );
  return flatten(actions).filter(action => action);
}
